import { useCallback, useState } from 'react';

import { submitPaymentLinkV2 } from '../../../../services/backendPaymentsV2Services';

import { captureException } from '../helpers/captureException';
import { buildPaymentPayload } from '../helpers/paymentPayload';
import {
  IPaymentLinkFormValues,
  ISubmitPaymentLinkOneTimePayment,
} from '../types/types';

const MISSING_PLAN_MESSAGE =
  'Não foi possível identificar o plano deste pagamento.';

interface IOneTimePaymentResult {
  method: 'pix' | 'boleto';
  data: ISubmitPaymentLinkOneTimePayment;
}

interface UsePaymentLinkSubmitParams {
  link: string;
  planId: string | undefined;
  reportError: (message: string | null) => void;
}

export const usePaymentLinkSubmit = ({
  link,
  planId,
  reportError,
}: UsePaymentLinkSubmitParams) => {
  const [isLoading, setIsLoading] = useState(false);
  const [paymentSubmitted, setPaymentSubmitted] = useState(false);
  const [paymentFailed, setPaymentFailed] = useState(false);
  const [oneTimePaymentResult, setOneTimePaymentResult] =
    useState<IOneTimePaymentResult | null>(null);

  const submitPayment = useCallback(
    async (values: IPaymentLinkFormValues) => {
      if (!planId) {
        reportError(MISSING_PLAN_MESSAGE);
        return;
      }

      setIsLoading(true);
      reportError(null);

      try {
        const payload = await buildPaymentPayload(values, String(planId));

        const { data } = await submitPaymentLinkV2(link, payload);

        const isOneTimeMethod = values.paymentMethodSelected !== 'credit_card';
        const oneTimePayment = data.oneTimePayment;

        if (isOneTimeMethod && oneTimePayment) {
          setOneTimePaymentResult({
            method: values.paymentMethodSelected as 'pix' | 'boleto',
            data: oneTimePayment,
          });
          return;
        }

        if (data.status === 'ACTIVE') {
          setPaymentSubmitted(true);
          return;
        }

        setPaymentFailed(true);
      } catch (error) {
        captureException('payment_submit', error);
        setPaymentFailed(true);
      } finally {
        setIsLoading(false);
      }
    },
    [link, planId, reportError],
  );

  const dismissFailure = useCallback(() => setPaymentFailed(false), []);

  const closeOneTimePayment = useCallback(
    () => setOneTimePaymentResult(null),
    [],
  );

  return {
    isLoading,
    paymentSubmitted,
    paymentFailed,
    oneTimePaymentResult,
    submitPayment,
    dismissFailure,
    closeOneTimePayment,
  };
};

export default usePaymentLinkSubmit;
